import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { FilePlus, FolderPlus, Plus } from 'lucide-react';
import type { ChatAddMenuProps } from './ChatAddMenu.types';
import { useTranslation } from 'react-i18next';

/**
 * The "+" composer entry point: opens a menu to attach a file, pick project context, or open the
 * agent's Connectors (MCP servers). Shown when the project_context_attachment flag is on OR the
 * agent has MCPs assigned; otherwise ChatInput keeps the legacy direct file-dialog trigger.
 */
export function ChatAddMenu(props: ChatAddMenuProps) {
  const { t } = useTranslation('chats');

  const { projectContextCount, showProjectContext = true, connectorsSlot, needsAttention } = props;

  return (
    <DropdownMenu open={props.open} onOpenChange={props.onOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="relative h-8 w-8 rounded-lg"
              aria-label={t('chats.chatAddMenu.add')}
              disabled={props.disabled}
            >
              <span className="toolbar-icon-hover">
                <Plus className="h-4 w-4" />
              </span>
              {needsAttention && (
                <span
                  className="absolute right-1 top-1 size-1.5 rounded-full bg-orange-500"
                  aria-hidden
                />
              )}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>{t('chats.chatAddMenu.add')}</TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={props.onAddFile}>
          <FilePlus className="h-4 w-4" />
          <span>{t('chats.chatAddMenu.addFiles')}</span>
        </DropdownMenuItem>
        {showProjectContext && (
          <DropdownMenuItem onClick={props.onAddProjectContext}>
            <FolderPlus className="h-4 w-4" />
            <span className="flex-1">{t('chats.chatAddMenu.addProjectContext')}</span>
            {projectContextCount > 0 && (
              <Badge variant="secondary" roundness="full">
                {projectContextCount}
              </Badge>
            )}
          </DropdownMenuItem>
        )}
        {connectorsSlot && <>{connectorsSlot}</>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
